import { API_BASE_URL } from "../../services/api";
import { authFetch } from "../../lib/authFetch";
import {
  SceneTemplateListQuery,
  SceneTemplateResponse,
  SceneTemplateServiceUnavailableError,
} from "../../interfaces/SceneTemplateApi";

/**
 * Thin REST client for `/scene-templates`. The endpoint is public so no
 * AccessKey is required, but we route through `authFetch` for consistency
 * (X-Client-App + credentials:include — the latter lets any session cookie
 * ride along for per-user personalization once the backend supports it).
 *
 * On 404 we surface `SceneTemplateServiceUnavailableError` so the
 * scene-picker modal can degrade gracefully on backends that haven't
 * deployed the migration yet.
 */

const buildQueryString = (query: SceneTemplateListQuery): string => {
  const params = new URLSearchParams();
  if (query.kind) params.set("kind", query.kind);
  if (query.designAspectRatio)
    params.set("designAspectRatio", query.designAspectRatio);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const handleNotOk = async (response: Response): Promise<never> => {
  if (response.status === 404) {
    throw new SceneTemplateServiceUnavailableError(
      404,
      "Scene template endpoint not yet available",
    );
  }
  let message: string | undefined;
  try {
    const data = await response.json();
    if (data && typeof data === "object") {
      message =
        typeof (data as { message?: unknown }).message === "string"
          ? (data as { message: string }).message
          : undefined;
    }
  } catch {
    /* ignore */
  }
  throw new Error(`HTTP ${response.status}: ${message ?? response.statusText}`);
};

class SceneTemplateService {
  private readonly base: string;

  constructor(base: string = API_BASE_URL) {
    this.base = base;
  }

  async list(
    query: SceneTemplateListQuery = {},
  ): Promise<SceneTemplateResponse[]> {
    const url = `${this.base}/scene-templates${buildQueryString(query)}`;
    // Sin AccessKeyGuard: el backend no anota la versión, así que no se espera
    // al IPC del arranque para mandarla (ver BuildAuthHeadersOptions).
    const response = await authFetch(url, { publicEndpoint: true });
    if (!response.ok) await handleNotOk(response);
    const payload = await response.json();
    if (Array.isArray(payload)) return payload as SceneTemplateResponse[];
    if (payload && Array.isArray(payload.data))
      return payload.data as SceneTemplateResponse[];
    return [];
  }
}

export const sceneTemplateService = new SceneTemplateService();
